import { type ReactNode, useEffect } from 'react';
import '../styles/globals.css';
import { usePageContext } from 'vike-react/usePageContext';
import { ClassicPageLayout } from '../components/layout/ClassicPageLayout';
import { ToolPageLayout } from '../components/layout/ToolPageLayout';
import { SkipLink } from '../components/layout/SkipLink';
import { CookieBanner } from '../components/layout/CookieBanner';
import { ScrollProgress } from '../components/layout/ScrollProgress';
import { Toaster } from '../components/ui/toast';
import { MobileStickyCta } from '../components/conversion/MobileStickyCta';
import { ResumeJourneyBanner } from '../components/conversion/ResumeJourneyBanner';
import { ExitIntentModal } from '../components/conversion/ExitIntentModal';
import { useConsent } from '../stores/consent';
import { useAudienceStore } from '../stores/audience';
import { initAnalytics, captureEvent } from '../lib/analytics';
import { initSentryClient } from '../lib/sentry';
import { persistAttribution, readAttribution } from '../lib/tracking';
import type { AudienceId } from '@shared/constants/audiences';

/** Pousse les signaux de consentement vers GTM Consent Mode v2. */
function pushGtmConsent(analytics: boolean, marketing: boolean) {
  if (typeof window === 'undefined') return;
  const dl = ((window as unknown as Record<string, unknown>).dataLayer as unknown[]) ?? [];
  const gtag = (...args: unknown[]) => dl.push(args);
  gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
}

export default function Layout({ children }: { children: ReactNode }) {
  const analyticsConsent = useConsent((s) => s.analytics);
  const marketingConsent = useConsent((s) => s.marketing);
  const hasResponded = useConsent((s) => s.hasResponded);
  const setLockedAudience = useAudienceStore((s) => s.setLockedAudience);
  const clearLock = useAudienceStore((s) => s.clearLock);

  useEffect(() => {
    if (hasResponded) {
      initAnalytics(analyticsConsent);
      pushGtmConsent(analyticsConsent, marketingConsent);
    }
  }, [analyticsConsent, marketingConsent, hasResponded]);

  // Init Sentry une seule fois au montage
  useEffect(() => {
    void initSentryClient();
  }, []);

  // Persiste les paramètres UTM/gclid/fbclid du premier atterrissage (M01)
  // et envoie l'event landing_view avec toutes les propriétés d'attribution.
  useEffect(() => {
    persistAttribution();
    // landing_view fire sur chaque navigation (SPA). La dé-duplication est gérée par PostHog.
    captureEvent('landing_view', {
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      ...readAttribution(),
    });
  }, []);

  const pageContext = usePageContext();
  const config = pageContext.config as { layout?: string; lockedAudience?: AudienceId };
  const layoutType = config.layout ?? 'classic';
  const lockedAudience = config.lockedAudience ?? null;

  // Synchronise le verrou d'audience avec le store à chaque navigation.
  // Si la page déclare `lockedAudience`, verrouille le store (et met à jour le cookie).
  // Sinon, déverrouille pour que les autres pages retrouvent leur comportement normal.
  useEffect(() => {
    if (lockedAudience) {
      setLockedAudience(lockedAudience);
    } else {
      clearLock();
    }
  }, [lockedAudience, setLockedAudience, clearLock]);

  return (
    <>
      <span id="top" aria-hidden="true" className="sr-only" />
      <SkipLink />
      {layoutType === 'classic' && <ScrollProgress />}
      <CookieBanner />
      <Toaster />
      {layoutType === 'tool' ? (
        <ToolPageLayout>{children}</ToolPageLayout>
      ) : (
        <ClassicPageLayout>{children}</ClassicPageLayout>
      )}

      {/* Leviers de conversion — pages de contenu uniquement, après réponse au bandeau cookies
          (évite tout chevauchement en bas d'écran avec la CookieBanner). */}
      {layoutType === 'classic' && hasResponded && (
        <>
          <MobileStickyCta />
          <ResumeJourneyBanner />
          <ExitIntentModal />
        </>
      )}
    </>
  );
}
